import { useEffect, useRef, useState } from "react";
import { SettingsRequestError, settingsApi } from "./api";
import type { UserCharacterImagePrompts, UserCharacterSummary } from "./types";
import "./voice-source-picker.css";

function imagePromptError(error: unknown, action: "load" | "save"): string {
  if (error instanceof SettingsRequestError) {
    if (error.status === 401) return "登录状态已失效，重新授权后再试。";
    if (error.status === 403) return "你没有为这个角色设置个人图片题词的权限。";
    if (error.status === 409) return "个人图片题词已在别处更新。草稿已保留，请载入最新版本后再试。";
    if (error.status === 429) return "图片题词服务暂时繁忙，请稍后重试。";
  }
  return action === "load" ? "无法读取个人图片题词，请稍后重试。" : "个人图片题词保存失败，草稿已保留，请稍后重试。";
}

export function ImagePromptPicker({ character, onClose, onSaved }: { character: UserCharacterSummary; onClose: () => void; onSaved?: ((prompts: UserCharacterImagePrompts) => void) | undefined }) {
  const [prompts, setPrompts] = useState<UserCharacterImagePrompts | null>(null);
  const [positivePrompt, setPositivePrompt] = useState<string | null>(null);
  const [negativePrompt, setNegativePrompt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const requestId = useRef(crypto.randomUUID());
  const dialogRef = useRef<HTMLElement>(null);

  function load(preserveDraft = false): void {
    setLoading(true); setError("");
    void settingsApi.loadUserCharacterImagePrompts(character.id).then((value) => {
      if (value.id !== character.id) throw new Error("user_character_image_prompts_mismatch");
      setPrompts(value);
      if (!preserveDraft) { setPositivePrompt(value.positive_prompt); setNegativePrompt(value.negative_prompt); }
      setNotice(preserveDraft ? "已载入最新版本，题词草稿仍保留。" : "");
    }).catch((cause) => setError(imagePromptError(cause, "load"))).finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
    const restoreTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.focus();
    return () => { restoreTarget?.focus(); };
  }, [character.id]);

  const positiveValue = positivePrompt ?? prompts?.shared_positive_prompt ?? "";
  const negativeValue = negativePrompt ?? prompts?.shared_negative_prompt ?? "";
  const inherited = positivePrompt === null && negativePrompt === null;
  const dirty = prompts !== null && (positivePrompt !== prompts.positive_prompt || negativePrompt !== prompts.negative_prompt);

  function editPositive(value: string): void {
    setPositivePrompt(value); setNegativePrompt((current) => current ?? prompts?.shared_negative_prompt ?? ""); setNotice("");
  }
  function editNegative(value: string): void {
    setPositivePrompt((current) => current ?? prompts?.shared_positive_prompt ?? ""); setNegativePrompt(value); setNotice("");
  }
  function resetToShared(): void { setPositivePrompt(null); setNegativePrompt(null); setNotice(""); }

  async function save(): Promise<void> {
    if (!prompts || !dirty || saving || (positivePrompt !== null && positivePrompt.trim().length === 0)) return;
    setSaving(true); setNotice("");
    try {
      const updated = await settingsApi.updateUserCharacterImagePrompts(character.id, positivePrompt, negativePrompt, prompts.image_prompt_revision, requestId.current);
      if (updated.id !== character.id) throw new Error("user_character_image_prompts_mismatch");
      setPrompts(updated); setPositivePrompt(updated.positive_prompt); setNegativePrompt(updated.negative_prompt); requestId.current = crypto.randomUUID();
      onSaved?.(updated);
      setNotice(updated.effective_source === "user" ? "已保存，仅对我生效。" : "已恢复共享题词。 ");
    } catch (cause) { setError(imagePromptError(cause, "save")); }
    finally { setSaving(false); }
  }

  return <div className="voice-source-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
    <section ref={dialogRef} className="voice-source-dialog" role="dialog" aria-modal="true" aria-labelledby="image-prompt-title" tabIndex={-1}>
      <header><div><h3 id="image-prompt-title">图片题词 · {character.name}</h3><p>个人覆盖只对你生效；恢复共享后会继续使用角色作者设置的正负题词。</p></div><button type="button" aria-label="关闭图片题词编辑" disabled={saving} onClick={onClose}>×</button></header>
      <div className="voice-source-body">
        {loading ? <p className="panel-state" role="status">正在读取个人图片题词…</p> : error && !prompts ? <div className="voice-source-error"><p className="action-error" role="alert">{error}</p><button className="secondary outlined" type="button" onClick={() => load()}>重试</button></div> : prompts ? <>
          <button className={`voice-source-default${inherited ? " selected" : ""}`} type="button" aria-pressed={inherited} disabled={saving} onClick={resetToShared}><strong>使用角色共享题词</strong><small>清除我的正向与负向题词覆盖；不会修改角色作者设置。</small></button>
          <p className="voice-source-catalog-note">当前生效：{inherited ? "角色共享题词" : "个人题词覆盖"}。正向题词不能为空；负向题词可以留空。</p>
          <label className="image-prompt-field">正向题词<textarea aria-label="个人正向题词" maxLength={4_000} value={positiveValue} aria-invalid={!inherited && positiveValue.trim().length === 0} onChange={(event) => editPositive(event.target.value)} /></label><small>{positiveValue.length} / 4000</small>
          <label className="image-prompt-field">负向题词<textarea aria-label="个人负向题词" maxLength={4_000} value={negativeValue} onChange={(event) => editNegative(event.target.value)} /></label><small>{negativeValue.length} / 4000</small>
        </> : null}
        {error && prompts ? <p className="action-error" role="alert">{error}</p> : null}
        {notice ? <p className="action-success" role="status">{notice}</p> : null}
      </div>
      <footer><button className="secondary outlined" type="button" disabled={saving} onClick={onClose}>关闭</button>{error.includes("已在别处更新") ? <button className="secondary outlined" type="button" disabled={loading || saving} onClick={() => load(true)}>载入最新版本（保留草稿）</button> : null}<button className="primary" type="button" disabled={!dirty || saving || (positivePrompt !== null && positiveValue.trim().length === 0)} onClick={() => void save()}>{saving ? "保存中…" : "保存个人题词"}</button></footer>
    </section>
  </div>;
}
